import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  canCommentVisitReport,
  canWriteVisitReport,
  visitPhotos,
  visitReportHasContent,
  type VisitReport,
} from "@/lib/planner/visitReport";
import {
  edgeErrorCode,
  fetchVisitReport,
  saveVisitReportEdit,
  visitErrorKey,
  visitReportQueryKey,
} from "@/lib/planner/visitReportApi";
import { FormSheet, FormSheetPrimaryButton } from "@/components/ui/FormSheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, CheckCircle2, XCircle, Clock, Camera, X, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ImgOffline } from "@/components/offline/ImgOffline";

const MAX_PHOTOS = 5;
const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.heic";

interface ChecklistItem {
  item: string;
  status: "ok" | "ko" | "pending";
}

interface VisitCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: any;
  onSuccess: () => void;
  editOnly?: boolean;
}

export function VisitCompletionDialog({ open, onOpenChange, task, onSuccess, editOnly = false }: VisitCompletionDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, company, role } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isInspection = task?.type === "inspection";

  /* EL INFORME SE LEE DE LA BASE, no de lo que trajo la pantalla (C3 / T07-01).
     El planificador del admin no pedía `visit_report`: el formulario salía vacío y
     «Guardar» borraba el informe del perito. Mientras no se haya leído, no se
     deja guardar; si no se puede leer, tampoco. */
  const reportQuery = useQuery({
    queryKey: visitReportQueryKey(task?.id),
    queryFn: () => fetchVisitReport(task.id),
    enabled: open && !!task?.id,
    staleTime: 0,
  });
  const row = reportQuery.data;
  const reportLoaded = !!row && !reportQuery.isFetching;
  const reportFailed = reportQuery.isError;

  const [observations, setObservations] = useState("");
  const [proposals, setProposals] = useState("");
  const [incidents, setIncidents] = useState("");
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [newItem, setNewItem] = useState("");

  // Photo state
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedPhotos, setRemovedPhotos] = useState<string[]>([]);

  // Se rellena UNA vez por apertura, con lo que hay en la base.
  const [filledFor, setFilledFor] = useState<string | null>(null);
  useEffect(() => {
    if (!open) { setFilledFor(null); return; }
    if (!row || reportQuery.isFetching || filledFor === row.id) return;
    const report = (row.visit_report ?? {}) as VisitReport;
    setObservations(report.observations || "");
    setProposals(report.proposals || "");
    setIncidents(report.incidents || "");
    setChecklist(Array.isArray(report.checklist) ? report.checklist : []);
    setExistingPhotos(visitPhotos(row));
    setNewFiles([]);
    setRemovedPhotos([]);
    setFilledFor(row.id);
  }, [open, row, reportQuery.isFetching, filledFor]);

  /* El informe es de quien tiene la visita asignada (A5). Al admin o al
     encargado que abre la visita de un perito se le explica, no se le deja
     escribir encima: para eso está el comentario de la empresa. */
  const canWrite = !!row && canWriteVisitReport(row, user?.id);
  const authorName = row?.assigned_to ? row.names[row.assigned_to] : undefined;
  /* P2 = A (Pablo, 26-09-2026): la empresa (admin o encargado) puede CERRAR la
     visita de un perito sin escribir su informe, o anularla (papelera). El
     informe sigue siendo del perito: la edge cierra sin tocarlo y deja quién y
     cuándo (`completed_by`), y el perito puede escribirlo después. Si el
     encargado no tiene esa nave en su alcance, la edge dice que no. */
  const canCloseWithoutReport =
    !editOnly && !!row && !canWrite && canCommentVisitReport(role) && row.status !== "completed" && row.status !== "cancelled";

  const totalPhotos = existingPhotos.filter(p => !removedPhotos.includes(p)).length + newFiles.length;
  const canAddMore = totalPhotos < MAX_PHOTOS;

  const addChecklistItem = () => {
    if (!newItem.trim()) return;
    setChecklist([...checklist, { item: newItem.trim(), status: "pending" }]);
    setNewItem("");
  };

  const updateItemStatus = (index: number, status: "ok" | "ko" | "pending") => {
    setChecklist(checklist.map((item, i) => i === index ? { ...item, status } : item));
  };

  const removeItem = (index: number) => {
    setChecklist(checklist.filter((_, i) => i !== index));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - totalPhotos;
    const toAdd = files.slice(0, remaining);
    setNewFiles(prev => [...prev, ...toAdd]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeExistingPhoto = (url: string) => {
    setRemovedPhotos(prev => [...prev, url]);
  };

  const removeNewFile = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = async (taskId: string): Promise<string[]> => {
    const uploadedUrls: string[] = [];
    /* La empresa sale de la fila y, si no, de la sesión. El planificador del
       perito no pide `company_id`, y al añadir una foto a un informe hecho
       salía «missing company» sin guardar nada (T15-04). */
    const companyId = row?.company_id || task?.company_id || company?.id;
    if (!companyId) throw new Error(t("perito.seguimiento.errors.notFound"));
    for (const file of newFiles) {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${companyId}/${taskId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("visit-photos")
        .upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data: urlData } = supabase.storage
        .from("visit-photos")
        .getPublicUrl(path);
      uploadedUrls.push(urlData.publicUrl);
    }
    return uploadedUrls;
  };

  const keptPhotoCount = existingPhotos.filter(p => !removedPhotos.includes(p)).length;
  const hasContent = visitReportHasContent(
    { observations, proposals, incidents, checklist: isInspection ? checklist : [] },
    keptPhotoCount + newFiles.length,
  );
  const canSubmit = reportLoaded && !reportFailed && canWrite && (editOnly || hasContent);

  const handleCloseWithoutReport = async () => {
    if (!canCloseWithoutReport) return;
    setLoading(true);
    try {
      // Sin `visit_report`: la edge no lo toca (C3) y solo apunta quién cerró.
      const response = await supabase.functions.invoke("update-task-status", {
        body: { task_id: task.id, new_status: "completed" },
      });
      if (response.error || (response.data as any)?.error) {
        const code = await edgeErrorCode(response);
        const key = visitErrorKey(code);
        throw new Error(key ? t(key) : ((response.data as any)?.error || (response.error as any)?.message || t("common.error")));
      }
      toast({ title: t("perito.seguimiento.closedWithoutReport") });
      queryClient.invalidateQueries({ queryKey: ["visit-report"] });
      queryClient.invalidateQueries({ queryKey: ["perito-seguimiento"] });
      queryClient.invalidateQueries({ queryKey: ["admin-seguimiento"] });
      onSuccess();
    } catch (err: any) {
      const key = visitErrorKey(null, err?.message);
      toast({ title: key ? t(key) : err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Sin la fila leída no se guarda NADA: guardar a ciegas es lo que borraba informes.
    if (!reportLoaded || !canWrite) return;
    if (!editOnly && !hasContent) {
      toast({ title: t("perito.seguimiento.emptyReport"), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // Upload new photos
      let uploadedUrls: string[] = [];
      if (newFiles.length > 0) {
        setUploading(true);
        uploadedUrls = await uploadPhotos(task.id);
        setUploading(false);
      }

      // Combine photos
      const keptPhotos = existingPhotos.filter(p => !removedPhotos.includes(p));
      const allPhotos = [...keptPhotos, ...uploadedUrls];

      /* Todas las claves del informe van SIEMPRE (vacías = se quitan): el servidor
         fusiona con lo que había y conserva el comentario de la empresa. */
      const visitReport = {
        observations: observations.trim(),
        proposals: proposals.trim(),
        incidents: incidents.trim(),
        checklist: isInspection ? checklist : [],
        photos: allPhotos,
      };

      if (editOnly) {
        await saveVisitReportEdit(task.id, visitReport, allPhotos);
      } else {
        const response = await supabase.functions.invoke("update-task-status", {
          body: { task_id: task.id, new_status: "completed", visit_report: visitReport },
        });
        if (response.error || (response.data as any)?.error) {
          const code = await edgeErrorCode(response);
          const key = visitErrorKey(code);
          throw new Error(key ? t(key) : ((response.data as any)?.error || (response.error as any)?.message || t("common.error")));
        }

        // Also update photos column
        if (allPhotos.length > 0) {
          await supabase
            .from("planner_tasks")
            .update({ photos: allPhotos })
            .eq("id", task.id);
        }
      }

      toast({ title: t("common.saved") });
      queryClient.invalidateQueries({ queryKey: ["visit-report"] });
      queryClient.invalidateQueries({ queryKey: ["perito-seguimiento"] });
      queryClient.invalidateQueries({ queryKey: ["admin-seguimiento"] });
      onSuccess();
    } catch (err: any) {
      const key = visitErrorKey(null, err?.message);
      toast({ title: key ? t(key) : err.message, variant: "destructive" });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  if (!task) return null;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={
        editOnly
          ? t("perito.seguimiento.editReport")
          : isInspection
            ? t("perito.seguimiento.completeInspection")
            : t("perito.seguimiento.completeVisit")
      }
      subtitle={task.title}
      isDirty={observations.length > 0 || proposals.length > 0 || incidents.length > 0 || newFiles.length > 0}
      footer={
        <FormSheetPrimaryButton onClick={handleSubmit} disabled={loading || uploading || !canSubmit}>
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-1" /> {t("perito.seguimiento.uploading")}</>
          ) : loading ? t("common.saving") : (
            editOnly
              ? t("common.save")
              : isInspection
                ? t("perito.seguimiento.completeInspection")
                : t("perito.seguimiento.completeVisit")
          )}
        </FormSheetPrimaryButton>
      }
    >

        <div className="space-y-5 py-2">
          {!reportLoaded && !reportFailed && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("perito.seguimiento.loadingReport")}
            </div>
          )}
          {reportFailed && (
            <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{t("perito.seguimiento.reportLoadError")}</span>
            </div>
          )}
          {reportLoaded && !canWrite && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
              <div className="space-y-2">
                <span className="block">
                  {authorName
                    ? t("perito.seguimiento.onlyAuthorNamed", { name: authorName })
                    : t("perito.seguimiento.onlyAuthor")}
                </span>
                {canCloseWithoutReport && (
                  <>
                    <span className="block text-amber-800">{t("perito.seguimiento.closeWithoutReportHint")}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleCloseWithoutReport()}
                      disabled={loading}
                      data-testid="visit-close-without-report"
                      className="bg-white"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                      {t("perito.seguimiento.closeWithoutReport")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
          {reportLoaded && canWrite && !editOnly && !hasContent && (
            <p className="text-xs text-muted-foreground">{t("perito.seguimiento.emptyReportHint")}</p>
          )}
          <fieldset disabled={!reportLoaded || !canWrite} className="space-y-5 disabled:opacity-60">
          {/* Observations */}
          <div>
            <Label>{t("perito.seguimiento.observations")}</Label>
            <Textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder={t("perito.seguimiento.observationsPlaceholder")}
              className="mt-1"
              rows={4}
            />
          </div>

          {/* Checklist (inspection only) */}
          {isInspection && (
            <div>
              <Label>{t("perito.seguimiento.checklist")}</Label>
              <div className="mt-2 space-y-2">
                {checklist.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-lg p-2">
                    <span className="flex-1 text-sm">{item.item}</span>
                    <div className="flex gap-1">
                      {(["ok", "ko", "pending"] as const).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => updateItemStatus(i, s)}
                          className={cn(
                            "p-1 rounded transition-colors",
                            item.status === s
                              ? s === "ok" ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                                : s === "ko" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                              : "text-muted-foreground hover:bg-muted"
                          )}
                        >
                          {s === "ok" ? <CheckCircle2 className="w-4 h-4" />
                            : s === "ko" ? <XCircle className="w-4 h-4" />
                            : <Clock className="w-4 h-4" />}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add new item */}
                <div className="flex gap-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder={t("perito.seguimiento.checklistItem")}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChecklistItem())}
                    className="flex-1"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addChecklistItem}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Proposals */}
          <div>
            <Label>{t("perito.seguimiento.proposals")}</Label>
            <Textarea
              value={proposals}
              onChange={(e) => setProposals(e.target.value)}
              placeholder={t("perito.seguimiento.proposalsPlaceholder")}
              className="mt-1"
              rows={3}
            />
          </div>

          {/* Incidents */}
          <div>
            <Label>{t("perito.seguimiento.incidents")}</Label>
            <Textarea
              value={incidents}
              onChange={(e) => setIncidents(e.target.value)}
              placeholder={t("perito.seguimiento.incidentsPlaceholder")}
              className="mt-1"
              rows={2}
            />
          </div>

          {/* Photos */}
          <div>
            <Label>{t("perito.seguimiento.photos")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("perito.seguimiento.maxPhotos")} ({totalPhotos}/{MAX_PHOTOS})
            </p>

            {/* Thumbnails grid */}
            {(existingPhotos.filter(p => !removedPhotos.includes(p)).length > 0 || newFiles.length > 0) && (
              <div className="grid grid-cols-5 gap-2 mt-2">
                {existingPhotos
                  .filter(p => !removedPhotos.includes(p))
                  .map((url) => (
                    <div key={url} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                      <ImgOffline src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeExistingPhoto(url)}
                        className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-destructive/80 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                {newFiles.map((file, i) => (
                  <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                    <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeNewFile(i)}
                      className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-destructive/80 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add photos button */}
            {canAddMore && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="w-4 h-4 mr-1" />
                {t("perito.seguimiento.addPhotos")}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
          </fieldset>
        </div>
    </FormSheet>
  );
}
